/**
 * AuthProvider — gate de autenticação com máquina de estados de sessão.
 *
 * Máquina de estados (Lista Canônica 4.2, design.md §16.5, Req 1/2/3):
 *   unauthenticated → authenticating → authenticated → expired → authenticating …
 *
 * Segurança (RNF 1, DD-003):
 * - O ID token NUNCA vai para localStorage (safeStorage barra tentativas).
 * - Token mantido em sessionStorage (expira com o fechamento da aba) + memória.
 * - Logout remove token de todos os storages.
 *
 * Anti-enumeração (Req 1.4, Req 3.2):
 * - Erros de login exibem mensagem genérica que não revela existência do e-mail.
 *
 * Integração (Req 1.2, DD-003):
 * - Usa `AuthPort` (interface estável). Em produção, injete `AzimAuthAdapter` — o IdP self-hosted do ADR-0017, que `main.tsx` seleciona. O `FirebaseAuthAdapter` que este comentário citava foi removido: era uma segunda implementação da mesma porta sobre um SDK que o portal nunca declarou como dependência.
 * - Em DEV, usa `DevAuthAdapter` (fake-jwks) ou, sem nenhum adapter configurado, o stub interno abaixo.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { setToken, clearToken, setSessionExpiredHandler } from "../services/ApiClient.js";
import type {
  AuthPort,
  AuthSession,
  InvitationAcceptance,
  PasswordResetConfirmation,
} from "../adapters/auth/AuthPort.js";
import { isOtpChallenge } from "../adapters/auth/AuthPort.js";
import { decodeTokenClaims } from "../shared/auth/tokenClaims.js";
import type { UserRole } from "../types/domain.js";
import { getProfile } from "../data/repos/settingsRepo.js";

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

/** Quatro estados canônicos de autenticação da SPA (Lista Canônica 4.2). */
export type SessionState =
  | "unauthenticated"
  | "authenticating"
  | "authenticated"
  | "expired";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  token: string;
  /**
   * Papel RBAC do usuário extraído do claim `role` do JWT (ADR-0015).
   * Opcional para compatibilidade retroativa com mocks de teste que não incluem o campo.
   * Fallback "sales_ae" (deny-by-default) quando ausente.
   */
  role?: UserRole;
}

/**
 * Resultado da primeira etapa do login:
 * - `{ requiresOtp: false }` → sessão autenticada (login direto).
 * - `{ requiresOtp: true, challengeId }` → segundo fator exigido (código por e-mail).
 */
export type LoginOutcome =
  | { requiresOtp: false }
  | { requiresOtp: true; challengeId: string };

export interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  /** Estado da máquina de sessão (Lista Canônica 4.2). */
  sessionState: SessionState;
  /**
   * Login com email + senha (anti-enumeração: erro genérico em falha).
   *
   * Quando o 2FA por e-mail está habilitado, a primeira etapa não autentica:
   * retorna `{ requiresOtp: true, challengeId }` e o código é enviado por e-mail.
   * O chamador deve então chamar `verifyOtp(challengeId, code)`.
   */
  login(email: string, password: string): Promise<LoginOutcome>;
  /** Segunda etapa do login (2FA): verifica o código OTP e conclui a autenticação. */
  verifyOtp(challengeId: string, code: string): Promise<void>;
  /**
   * Primeiro acesso via convite (Req 2): consome o token do e-mail, define nome de exibição e senha, e devolve o e-mail do convidado.
   * NÃO autentica — o login real, com segundo fator, vem em seguida e é ele que prova, no primeiro uso, que a credencial recém-criada funciona.
   */
  acceptInvitation(
    token: string,
    displayName: string,
    password: string,
  ): Promise<InvitationAcceptance>;
  /** Solicita link de redefinição de senha (anti-enumeração: Req 3.2). */
  requestPasswordReset(email: string): Promise<void>;
  /**
   * Conclui a redefinição (Req 3.2, segunda metade): consome o token do e-mail, grava a senha nova e devolve o e-mail da conta.
   * NÃO autentica — o login real, com segundo fator, vem em seguida e é ele que prova, no primeiro uso, que a senha nova funciona.
   */
  confirmPasswordReset(token: string, password: string): Promise<PasswordResetConfirmation>;
  /** Logout: remove token de TODOS os storages e transiciona para unauthenticated. */
  logout(): void;
}

// ---------------------------------------------------------------------------
// Contexto
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return ctx;
}

// ---------------------------------------------------------------------------
// Armazenamento seguro de sessão (sessionStorage — nunca localStorage)
// ---------------------------------------------------------------------------

const SESSION_KEY = "azim.session";

/** Papéis válidos para guard na conversão do token (lista canônica ADR-0015). */
const VALID_ROLES: ReadonlySet<string> = new Set<UserRole>([
  "platform_operator",
  "tenant_admin",
  "bu_manager",
  "sales_ae",
  "sales_sdr",
  "sales_se",
  "viewer",
]);

function persistSession(user: AuthUser): void {
  // sessionStorage: expira quando a aba/janela é fechada (DD-003).
  //
  // O try/catch não é decoração. Esta função roda DENTRO do observador de estado de sessão, que
  // por sua vez roda dentro da renovação de token do adapter. Uma exceção aqui — quota estourada,
  // ou SecurityError em contexto de armazenamento particionado ou bloqueado — subia pelo notify e
  // derrubava uma renovação que o servidor já tinha concluído, com o refresh token anterior já
  // revogado. O `persistToStorage` do próprio adapter sempre esteve protegido contra exatamente
  // isso; este aqui não estava. Perder a persistência custa um novo login ao reabrir a aba;
  // perder a renovação custa a sessão na hora.
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(user));
  } catch (erro) {
    console.error("[auth] não foi possível persistir a sessão no sessionStorage", erro);
  }
}

function loadSession(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthUser;
    // Compatibilidade retroativa: se role não está no objeto salvo (sessão antiga
    // ou objeto sem role), extrai do token JWT para garantir RBAC correto.
    if (!parsed.role && parsed.token) {
      const claims = decodeTokenClaims(parsed.token);
      if (typeof claims.role === "string" && VALID_ROLES.has(claims.role)) {
        parsed.role = claims.role as UserRole;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

function clearSession(): void {
  // Simétrico ao persistSession: removeItem lança nas mesmas condições que setItem, e este
  // caminho também roda de dentro do observador de estado de sessão.
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch (erro) {
    console.error("[auth] não foi possível limpar a sessão do sessionStorage", erro);
  }
}

function sessionToUser(session: AuthSession): AuthUser {
  // Extrai o papel do claim `role` do JWT (ADR-0015, decodeTokenClaims).
  // Fallback "sales_ae" garante deny-by-default para rotas restritas.
  const claims = decodeTokenClaims(session.token);
  const role: UserRole =
    typeof claims.role === "string" && VALID_ROLES.has(claims.role)
      ? (claims.role as UserRole)
      : "sales_ae";

  return {
    id: session.uid,
    email: session.email,
    displayName: session.email.split("@")[0] ?? session.email,
    token: session.token,
    role,
  };
}

// ---------------------------------------------------------------------------
// Stub DEV (usado quando nenhum AuthPort é injetado)
// ---------------------------------------------------------------------------

const DEV_USER: AuthUser = {
  id: "u-ana",
  email: "ana.rocha@vellus.tech",
  displayName: "Ana Beatriz",
  token: "dev-token-insecure",
  role: "tenant_admin",
};

/** Stub DEV que implementa AuthPort com delays simulados. */
const devAuthStub: AuthPort = {
  async signIn(email: string, _password: string) {
    await new Promise<void>((r) => setTimeout(r, 650));
    return { uid: "u-dev", email, token: "dev-token-insecure" };
  },
  async verifyOtp(_challengeId: string, _code: string) {
    await new Promise<void>((r) => setTimeout(r, 300));
    return { uid: "u-dev", email: DEV_USER.email, token: "dev-token-insecure" };
  },
  async signOut() {
    await new Promise<void>((r) => setTimeout(r, 100));
  },
  async getIdToken() {
    return DEV_USER.token;
  },
  async acceptInvitation(_token: string, _displayName: string, _password: string) {
    await new Promise<void>((r) => setTimeout(r, 700));
    return { email: DEV_USER.email };
  },
  async requestPasswordReset(_email: string) {
    await new Promise<void>((r) => setTimeout(r, 500));
  },
  async confirmPasswordReset(_token: string, _password: string) {
    await new Promise<void>((r) => setTimeout(r, 700));
    return { email: DEV_USER.email };
  },
  onAuthStateChanged(callback) {
    const session = loadSession();
    if (session) {
      callback({ uid: session.id, email: session.email, token: session.token });
    }
    return () => {};
  },
};

// ---------------------------------------------------------------------------
// AuthProvider
// ---------------------------------------------------------------------------

export interface AuthProviderProps {
  children: ReactNode;
  /** Adapter de autenticação. Omitir usa o stub DEV. */
  authAdapter?: AuthPort;
}

export function AuthProvider({ children, authAdapter }: AuthProviderProps) {
  const adapter = authAdapter ?? devAuthStub;

  const [user, setUser] = useState<AuthUser | null>(() => {
    const session = loadSession();
    if (session) {
      setToken(session.token);
      return session;
    }
    return null;
  });

  const [sessionState, setSessionState] = useState<SessionState>(() =>
    loadSession() ? "authenticated" : "unauthenticated"
  );

  // Sessão morta anunciada pelo ApiClient: um 401 que SOBREVIVEU ao refresh-on-401.
  //
  // Este effect é o que torna o estado `expired` alcançável. Ele estava declarado no tipo, o
  // `RequireAuth` já o tratava mandando o usuário para o login, e ninguém jamais o atribuía — o
  // resultado era a sessão zumbi vista em produção: a interface mostrando o nome e o papel do
  // usuário enquanto toda chamada devolvia 401, e cada tela exibindo o seu erro genérico com um
  // "Tentar novamente" que não podia funcionar, porque o problema não era a rota.
  //
  // A sessão persistida é limpa junto: sem isso, um F5 leria o `azim.session` do storage e
  // voltaria direto para `authenticated`, ressuscitando o zumbi.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      // `adapter.signOut()` é o que apaga `azim.auth.access` e `azim.auth.refresh` do
      // sessionStorage. Sem ele, o parágrafo acima estava errado: `clearSession()` limpa apenas
      // `azim.session`, e no próximo carregamento o AzimAuthAdapter re-hidratava a sessão a partir
      // das chaves sobreviventes, devolvendo o usuário ao estado `authenticated` com o mesmo token
      // recusado. O zumbi voltava no F5 (LDG-0184).
      void adapter.signOut();
      clearToken();
      clearSession();
      setUser(null);
      setSessionState("expired");
    });
    return () => setSessionExpiredHandler(null);
  }, [adapter]);

  // Observa mudanças externas de estado do Firebase (tab reload, token expirado)
  useEffect(() => {
    const unsubscribe = adapter.onAuthStateChanged((session) => {
      if (session) {
        const authUser = sessionToUser(session);
        setToken(authUser.token);
        persistSession(authUser);
        setUser(authUser);
        setSessionState("authenticated");
      } else {
        clearToken();
        clearSession();
        setUser(null);
        setSessionState("unauthenticated");
      }
    });
    return unsubscribe;
  }, [adapter]);

  // Carrega o perfil real após autenticação e atualiza displayName com o valor
  // do backend (/v1/auth/me). Fallback mantido no sessionToUser (derivação do email)
  // é usado inicialmente.
  //
  // A dependência é o id estável do usuário (não o objeto): buscar o perfil
  // troca o displayName via setUser, o que cria um novo objeto `user` — se o
  // effect dependesse de `user`, isso re-dispararia getProfile em loop
  // (observado como 429 Too Many Requests em /v1/auth/me). A guarda de igualdade
  // evita reescrever quando o nome já é o real.
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId || sessionState !== "authenticated") {
      return;
    }
    let cancelled = false;
    void getProfile()
      .then((profile) => {
        if (cancelled || !profile.displayName) {
          return;
        }
        let toPersist: AuthUser | null = null;
        setUser((current) => {
          if (!current || current.displayName === profile.displayName) {
            return current;
          }
          toPersist = { ...current, displayName: profile.displayName };
          return toPersist;
        });
        if (toPersist) {
          persistSession(toPersist);
        }
      })
      .catch(() => {
        // Falha ao carregar perfil: mantém displayName fallback (derivado do email)
      });
    return () => {
      cancelled = true;
    };
  }, [userId, sessionState]);

  const isLoading = sessionState === "authenticating";

  /**
   * Login com anti-enumeração (Req 1.4): qualquer falha resulta em mensagem
   * genérica que não revela se o e-mail existe ou não.
   */
  const login = useCallback(
    async (email: string, password: string): Promise<LoginOutcome> => {
      setSessionState("authenticating");
      try {
        const result = await adapter.signIn(email, password);

        // 2FA habilitado: a 1ª etapa não autentica — devolve o desafio ao chamador.
        // Volta ao estado não-autenticado: a sessão só existe após verifyOtp.
        if (isOtpChallenge(result)) {
          setSessionState("unauthenticated");
          return { requiresOtp: true, challengeId: result.challengeId };
        }

        const authUser = sessionToUser(result);
        setToken(authUser.token);
        persistSession(authUser);
        setUser(authUser);
        setSessionState("authenticated");
        return { requiresOtp: false };
      } catch {
        // Anti-enumeração: erro genérico — não revela existência do e-mail.
        setSessionState("unauthenticated");
        throw new Error("Credenciais inválidas. Verifique e tente novamente.");
      }
    },
    [adapter]
  );

  /**
   * Segunda etapa do login (2FA): verifica o código OTP do desafio. Em caso de
   * falha, repassa a mensagem do adapter (código inválido/expirado, lockout) para
   * exibição pt-BR na UI.
   */
  const verifyOtp = useCallback(
    async (challengeId: string, code: string): Promise<void> => {
      setSessionState("authenticating");
      try {
        const session = await adapter.verifyOtp(challengeId, code);
        const authUser = sessionToUser(session);
        setToken(authUser.token);
        persistSession(authUser);
        setUser(authUser);
        setSessionState("authenticated");
      } catch (err) {
        setSessionState("unauthenticated");
        throw err instanceof Error
          ? err
          : new Error("Código inválido ou expirado.");
      }
    },
    [adapter]
  );

  /**
   * Aceite de convite (Req 2): delega ao adapter e devolve o e-mail do convidado.
   *
   * Duas ausências são deliberadas.
   * A primeira é a transição da máquina de sessão: o aceite não emite token, então o convidado segue anônimo até passar pelo login.
   * A segunda é o try/catch que homogeneíza o erro; o `InvitationError` do adapter chega intacto à tela porque é o `reason` dele que decide se o convidado deve pedir um convite novo, corrigir a senha ou tentar de novo em alguns minutos.
   * Traduzir tudo para uma frase só, como o login faz por anti-enumeração, aqui só produziria um convidado sem saída.
   */
  const acceptInvitation = useCallback(
    async (
      token: string,
      displayName: string,
      password: string,
    ): Promise<InvitationAcceptance> => adapter.acceptInvitation(token, displayName, password),
    [adapter]
  );

  /**
   * Reset de senha com anti-enumeração (Req 3.2): a resposta é sempre genérica,
   * independentemente de o e-mail existir ou não. O erro interno é suprimido
   * propositalmente.
   */
  const requestPasswordReset = useCallback(
    async (email: string): Promise<void> => {
      try {
        await adapter.requestPasswordReset(email);
      } catch {
        // Suprimido intencionalmente: anti-enumeração (Req 3.2).
        // O chamador exibe sempre a mesma mensagem genérica.
      }
    },
    [adapter]
  );

  /**
   * Confirmação da redefinição de senha.
   *
   * Sem try/catch, ao contrário de `requestPasswordReset` logo acima, e a assimetria é o ponto: a anti-enumeração governa o PEDIDO, onde qualquer pessoa digita um e-mail e a resposta não pode revelar quem existe. Aqui quem chegou já provou posse do link, e é o `reason` do `PasswordResetError` que decide se a tela manda corrigir a senha, pedir outro link ou tentar de novo em minutos.
   * Engolir o erro aqui devolveria a mesma tela genérica dos dois casos e faria a pessoa repetir a senha recusada até desistir — que é o sintoma que esta correção existe para acabar.
   */
  const confirmPasswordReset = useCallback(
    async (token: string, password: string): Promise<PasswordResetConfirmation> =>
      adapter.confirmPasswordReset(token, password),
    [adapter]
  );

  /**
   * Logout: remove token de TODOS os storages (sessionStorage + memória) e
   * transiciona para unauthenticated (RNF 1.3).
   */
  const logout = useCallback((): void => {
    void adapter.signOut().catch(() => {});
    clearToken();
    clearSession();
    setUser(null);
    setSessionState("unauthenticated");
  }, [adapter]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      sessionState,
      login,
      verifyOtp,
      acceptInvitation,
      requestPasswordReset,
      confirmPasswordReset,
      logout,
    }),
    [
      user,
      isLoading,
      sessionState,
      login,
      verifyOtp,
      acceptInvitation,
      requestPasswordReset,
      confirmPasswordReset,
      logout,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
